import "../sass/components/processing.scss"
import {useParams, useLocation, useHistory} from "react-router-dom";
import {useEffect, useState, useContext} from "react";
import {fetchStripeCheckoutSessionStatus, fetchStripeRetryConfirmation} from "../apiRequests/api";
import "../sass/components/content.scss"
import {useDispatch} from "react-redux";
import Loading from './Loading';
import {useAuth0} from "@auth0/auth0-react";
import {SigninContext} from "../contexts/SigninContext";

export default function PaymentConfirmed() {
    const dispatch = useDispatch()
    const history = useHistory()
    const {sessionId} = useParams()
    const location = useLocation()
    const isBalancePayment = new URLSearchParams(location.search).get('type') === 'balance'
    const {userName} = useContext(SigninContext)
    const {getIdTokenClaims} = useAuth0()
    const [status, setStatus] = useState('checking')
    const [paymentData, setPaymentData] = useState(null)
    const [retryCount, setRetryCount] = useState(0)
    const [errorMessage, setErrorMessage] = useState('')
    const [retrying, setRetrying] = useState(false)

    useEffect(() => {
        let intervalId = null
        let timeoutId = null
        let attempts = 0
        const maxAttempts = 60

        const checkStatus = async () => {
            try {
                const token = await getIdTokenClaims()
                const data = await fetchStripeCheckoutSessionStatus(
                    { sessionId: sessionId, customerAccount: userName },
                    token.__raw
                )

                if (data.status === 'not_found') {
                    attempts++
                    if (attempts >= maxAttempts) {
                        setStatus('error')
                        setErrorMessage('Payment confirmation is taking longer than expected')
                        if (intervalId) clearInterval(intervalId)
                        if (timeoutId) clearTimeout(timeoutId)
                    }
                    return
                }

                setPaymentData(data)
                setRetryCount(data.retryCount || 0)

                if (data.status === 'confirmed') {
                    setStatus('confirmed')
                    if (!isBalancePayment) {
                        dispatch({type: "CLEAR_CART", payload: []})
                    }
                    if (intervalId) clearInterval(intervalId)
                    if (timeoutId) clearTimeout(timeoutId)
                    if (isBalancePayment) {
                        setTimeout(() => history.push('/account'), 4000)
                    }
                } else if (data.status === 'failed' || data.status === 'manual_intervention_required') {
                    setStatus('failed')
                    setErrorMessage(data.lastError || 'An error occurred while processing your payment')
                    if (intervalId) clearInterval(intervalId)
                    if (timeoutId) clearTimeout(timeoutId)
                } else if (data.status === 'processing' || data.status === 'pending') {
                    setStatus('processing')
                }
            } catch (error) {
                console.error('Error checking payment status:', error)
                attempts++
                if (attempts >= maxAttempts) {
                    setStatus('error')
                    setErrorMessage('Unable to verify payment status. Please contact support.')
                    if (intervalId) clearInterval(intervalId)
                    if (timeoutId) clearTimeout(timeoutId)
                }
            }
        }

        checkStatus()
        intervalId = setInterval(checkStatus, 2000)
        timeoutId = setTimeout(() => {
            setStatus('error')
            setErrorMessage('Payment confirmation is taking longer than expected. Please contact support to verify your payment.')
            if (intervalId) clearInterval(intervalId)
        }, 120000)

        return () => {
            if (intervalId) clearInterval(intervalId)
            if (timeoutId) clearTimeout(timeoutId)
        }
    }, [sessionId, dispatch, userName, getIdTokenClaims, isBalancePayment, history])

    const handleRetry = async () => {
        setRetrying(true)
        try {
            const token = await getIdTokenClaims()
            const result = await fetchStripeRetryConfirmation(
                { sessionId: sessionId, customerAccount: userName },
                token.__raw
            )
            if (result.status === 'confirmed') {
                setStatus('confirmed')
                if (!isBalancePayment) {
                    dispatch({type: "CLEAR_CART", payload: []})
                }
                if (isBalancePayment) {
                    setTimeout(() => history.push('/account'), 4000)
                }
            } else {
                setErrorMessage(result.message || 'Retry failed. Please contact support.')
            }
        } catch (error) {
            setErrorMessage('Unable to retry. Please contact support.')
        } finally {
            setRetrying(false)
        }
    }

    const renderContent = () => {
        if (isBalancePayment) {
            return renderBalanceContent()
        }
        return renderOrderContent()
    }

    const renderBalanceContent = () => {
        switch (status) {
            case 'checking':
            case 'processing':
                return (
                    <>
                        <h1 className='content-page-title'>Processing Payment</h1>
                        <p>Please wait while we confirm your payment...</p>
                    </>
                )

            case 'confirmed':
                return (
                    <>
                        <h1 className='content-page-title'>Payment Confirmed</h1>
                        <p>Your balance payment has been received successfully.</p>
                        <p style={{fontSize: '0.9em', color: '#666'}}>Returning you to your account...</p>
                    </>
                )

            case 'failed':
                return (
                    <>
                        <h1 className="content-page-title">Payment Issue</h1>
                        <p>Your payment was received, but there was an issue applying it to your account.</p>
                        <p><strong>You will not be charged twice.</strong></p>
                        <p>Please contact us and quote reference: <strong>{sessionId}</strong></p>
                        {errorMessage && <p style={{fontSize: '0.9em', color: '#666'}}>{errorMessage}</p>}
                        <button onClick={handleRetry} disabled={retrying}>
                            {retrying ? 'Retrying...' : 'Retry'}
                        </button>
                        <br/>
                        <a href="/account">Return to Account</a>
                    </>
                )

            case 'error':
            default:
                return (
                    <>
                        <h1 className='content-page-title'>Payment Processing</h1>
                        <p>We're still confirming your payment. You can safely return to your account.</p>
                        <p>If your balance hasn't updated within 10 minutes, please contact us with reference: <strong>{sessionId}</strong></p>
                        <a href="/account">Return to Account</a>
                    </>
                )
        }
    }

    const renderOrderContent = () => {
        switch (status) {
            case 'checking':
            case 'processing':
                return (
                    <>
                        <h1 className='content-page-title'>Processing Your Order</h1>
                        <p>Please wait while we confirm your order...</p>
                        {retryCount > 1 && (
                            <p style={{fontSize: '0.9em', color: '#666'}}>
                                This is taking a bit longer than usual. Please don't close this page.
                            </p>
                        )}
                    </>
                )

            case 'confirmed':
                return (
                    <>
                        <h1 className='content-page-title'>Order Confirmed</h1>
                        <p>Thank you for your order and payment</p>
                        {paymentData?.orderId && paymentData.orderId !== 'Balance' && (
                            <p>Order Number: {paymentData.orderId}</p>
                        )}
                    </>
                )

            case 'failed':
                return paymentData?.status === 'manual_intervention_required' ? (
                    <>
                        <h1 className="content-page-title">Order Confirmation Issue</h1>
                        <p>Your payment was received, but we encountered an issue confirming your order in our system.</p>
                        <p><strong>Don't worry - you will not be charged twice.</strong></p>
                        <p>Our team has been notified and will process your order manually. You will receive a confirmation email shortly.</p>
                        <p>Reference: {sessionId}</p>
                        {errorMessage && (
                            <div>
                                <strong>Technical Details</strong>
                                <p>{errorMessage}</p>
                            </div>
                        )}
                        <button onClick={handleRetry} disabled={retrying}>
                            {retrying ? 'Retrying...' : 'Retry Order Confirmation'}
                        </button>
                    </>
                ) : (
                    <>
                        <h1 className="content-page-title">Processing Your Order</h1>
                        <p>We encountered a hiccup confirming your order, but we're retrying automatically.</p>
                        <p><strong>Don't worry - you will not be charged twice.</strong></p>
                        <p>Please keep this page open. If this takes more than 10 minutes, contact support with reference: <strong>{sessionId}</strong></p>
                    </>
                )

            case 'error':
            default:
                return (
                    <>
                        <h1 className='content-page-title'>Verification In Progress</h1>
                        <p>We're still processing your order confirmation.</p>
                        <p>
                            This can take a few minutes. You can safely close this page -
                            you will receive an email confirmation once your order is processed.
                        </p>
                        <p>
                            If you don't receive confirmation within 10 minutes, please contact support
                            with reference: <strong>{sessionId}</strong>
                        </p>
                    </>
                )
        }
    }

    return (
        <div className="page processing-page">
            {paymentData === null && status === 'checking' ? (
                <Loading/>
            ) : (
                <>
                    <div className="title">
                        {renderContent()}
                    </div>
                    {!isBalancePayment && (
                        <div className="subtitle">
                            To view your transactions, please go to <a href="/account">My Account</a>.
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
